import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import NavBar from '../components/layout/NavBar'
import { getToken } from '../lib/auth'
import { useAIEditor } from '../hooks/useAIEditor'
import { createStoreProduct, fetchListingDetail, updateStoreListing } from '../api/store'
import { api } from '../lib/api'
import { useMe } from '../hooks/useMe'
import RichEditor, { type EditorToolbarState, type RichEditorHandle } from '../components/editor/RichEditor'
import { generateCoverImage } from '../api/ai'
import { uploadCoverDirect } from '../api/posts'
import { FiBold, FiItalic, FiUnderline, FiType, FiList, FiLink, FiImage, FiX, FiCode, FiMinus, FiRotateCcw, FiRotateCw, FiAlignLeft, FiAlignCenter, FiAlignRight, FiAlignJustify } from 'react-icons/fi'
import { RiListOrdered, RiDoubleQuotesL, RiStrikethrough2, RiMarkPenLine } from 'react-icons/ri'
import { BiCodeBlock } from 'react-icons/bi'
import { MdFormatClear } from 'react-icons/md'
import DraftControls from '../features/editor/DraftControls'

const STORE_POLISH_DEFAULT = 'studio background, soft light, minimal shadows'
const STORE_DRAFT_INDEX_KEY = 'store_drafts:index'
const STORE_DRAFT_CURRENT_KEY = 'store_drafts:current_id'
const STORE_DRAFT_ITEM_PREFIX = 'store_drafts:item:'

export default function StoreListEditor() {
  const authed = Boolean(getToken())
  const nav = useNavigate()
  const { me } = useMe(authed)
  const ai = useAIEditor()

  const [title, setTitle] = useState('')
  const [subtitle, setSubtitle] = useState('')
  const [price, setPrice] = useState<string>('')
  const [stock, setStock] = useState<string>('1')
  const [condition, setCondition] = useState<'new' | 'used'>('used')
  const [productType, setProductType] = useState<'physical' | 'digital' | 'hybrid'>('physical')
  // Rich description
  const [descriptionHtml, setDescriptionHtml] = useState('<p></p>')
  const [descriptionPlain, setDescriptionPlain] = useState('')
  const richRef = useRef<RichEditorHandle | null>(null)
  const [tb, setTb] = useState<EditorToolbarState>({
    bold: false, italic: false, underline: false, strike: false, code: false, highlight: false, link: false,
    h1: false, h2: false, h3: false, h4: false, bullet: false, ordered: false, quote: false, codeBlock: false,
    align: 'left', canUndo: false, canRedo: false,
  })
  const [images, setImages] = useState<string[]>([])
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sellerBlockers, setSellerBlockers] = useState<Array<{ code: string; message: string }>>([])
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [message, setMessage] = useState<string | null>(null)
  const [editingListingId, setEditingListingId] = useState<string | null>(null)
  const heroImage = images[selectedImageIndex] || ''
  const imageError = fieldErrors.image_url || fieldErrors.gallery
  const eligibility = me?.seller_eligibility
  const publishBlocked = Boolean(eligibility && !eligibility.can_publish)
  const blockers = sellerBlockers.length ? sellerBlockers : (eligibility?.blockers || [])
  const saveLabel = editingListingId ? 'Update listing' : (publishBlocked ? 'Save draft' : 'Publish listing')

  // AI description helpers
  const [tone, setTone] = useState<'Professional' | 'Friendly' | 'Enthusiastic' | 'Minimal'>('Professional')
  const [aiPrompt, setAiPrompt] = useState('')
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [aiSuggestion, setAiSuggestion] = useState('')
  const [titleOptions, setTitleOptions] = useState<string[]>([])
  const [priceSuggest, setPriceSuggest] = useState<string | null>(null)

  // AI image helper
  const [imgPrompt, setImgPrompt] = useState(STORE_POLISH_DEFAULT)
  const [aiImages, setAiImages] = useState<Array<{ url?: string; b64_json?: string; public_url?: string }>>([])
  const [aiImageLoading, setAiImageLoading] = useState(false)
  const [aiImageError, setAiImageError] = useState<string | null>(null)
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null)
  const [draftsList, setDraftsList] = useState<Array<{ id: string; title: string; updated_at: string }>>([])
  const [loadingListing, setLoadingListing] = useState(false)
  const [listingActive, setListingActive] = useState(true)
  const [togglingActive, setTogglingActive] = useState(false)

  // Seed from profile lightly
  useEffect(() => {
    if (!title && me?.profile?.handle) {
      setTitle('Product title')
    }
  }, [me, title])

  // Template presets from /store/list/new?template=...
  const [searchParams] = useSearchParams()
  const listingParam = useMemo(() => searchParams.get('listing'), [searchParams])
  const templateParam = useMemo(() => (searchParams.get('template') || '').toLowerCase(), [searchParams])
  useEffect(() => {
    if (listingParam) return
    const t = templateParam
    if (!t) return
    if (t === 'used') {
      if (!title) setTitle('Used gear — great condition')
      if (!subtitle) setSubtitle('Ready for a new home')
      setCondition('used')
      setProductType('physical')
      if (!imgPrompt) setImgPrompt('product on neutral studio background, soft light, minimal shadows')
      if (!descriptionPlain) seedDescription('Well cared for, lightly used. Includes all the essentials listed, see photos for details. Ships securely.')
    } else if (t === 'apparel') {
      if (!title) setTitle('New apparel / merch')
      setCondition('new')
      setProductType('physical')
      if (!imgPrompt) setImgPrompt('flat lay on clean background, soft even lighting')
      if (!descriptionPlain) seedDescription('Brand new. Choose your size and color. Comfortable fit and durable fabric. Ships in 2–3 days.')
    } else if (t === 'electronics') {
      if (!title) setTitle('Refurb / like‑new electronics')
      setCondition('used')
      setProductType('physical')
      if (!imgPrompt) setImgPrompt('product on seamless white background, slight reflection, professional')
      if (!descriptionPlain) seedDescription('Thoroughly tested and cleaned. Includes listed accessories. Minor cosmetic wear, 100% functional.')
    } else if (t === 'digital') {
      if (!title) setTitle('Digital download / license')
      setCondition('new')
      setProductType('digital')
      if (!imgPrompt) setImgPrompt('clean promo graphic, minimal, gradient background')
      if (!descriptionPlain) seedDescription('Instant digital delivery after purchase. Personal-use license included. No physical item will be shipped.')
    } else if (t === 'service') {
      if (!title) setTitle('Service: session or package')
      setCondition('new')
      setProductType('digital')
      if (!imgPrompt) setImgPrompt('friendly service promo image, minimal background')
      if (!descriptionPlain) seedDescription('Book a session or package. After purchase, we’ll coordinate time and details. Includes the items listed.')
    }
  }, [listingParam, templateParam])

  useEffect(() => {
    if (!listingParam) {
      setEditingListingId(null)
      setLoadingListing(false)
      setListingActive(true)
      return
    }
    setEditingListingId(listingParam)
    loadListingForEdit(listingParam)
  }, [listingParam])

  useEffect(() => {
    const idx = loadStoreDraftsIndex()
    setDraftsList(idx)
    if (listingParam) {
      setCurrentDraftId(null)
      localStorage.removeItem(STORE_DRAFT_CURRENT_KEY)
      return
    }
    const currentId = localStorage.getItem(STORE_DRAFT_CURRENT_KEY)
    if (currentId) {
      const draft = loadStoreDraft(currentId)
      if (draft) applyDraft(draft, currentId)
    }
  }, [listingParam])

  function seedDescription(text: string) {
    const html = textToHtmlParagraphs(text)
    setDescriptionHtml(html)
    setDescriptionPlain(text)
    try { richRef.current?.setHTML(html) } catch {}
  }

  function parsePrice(v: string): number | null {
    const n = parseFloat((v || '').replace(/[^0-9.]/g, ''))
    return isFinite(n) ? n : null
  }

  function pickFieldError(value: any): string | null {
    if (!value) return null
    if (Array.isArray(value) && value.length) return String(value[0])
    if (typeof value === 'string') return value
    return null
  }

  function extractFieldErrors(data: any): Record<string, string> {
    if (!data || typeof data !== 'object') return {}
    const next: Record<string, string> = {}
    Object.entries(data).forEach(([key, value]) => {
      if (key === 'detail' || key === 'error' || key === 'non_field_errors') return
      const msg = pickFieldError(value)
      if (msg) next[key] = msg
    })
    return next
  }

  const normalizeImages = (list: string[]): string[] => {
    const result: string[] = []
    for (const raw of list) {
      const clean = (raw || '').trim()
      if (!clean) continue
      if (!result.includes(clean)) result.push(clean)
    }
    return result
  }

  function setImagesDirect(list: string[] | null | undefined, desiredIndex = 0) {
    const normalized = normalizeImages(Array.isArray(list) ? list : [])
    setImages(normalized)
    if (!normalized.length) {
      setSelectedImageIndex(0)
    } else {
      const nextIndex = Math.max(0, Math.min(desiredIndex, normalized.length - 1))
      setSelectedImageIndex(nextIndex)
    }
  }

  function addListingImage(url: string, makePrimary = false) {
    const clean = (url || '').trim()
    if (!clean) return
    setImages((prev) => {
      const rest = prev.filter((u) => u !== clean)
      const next = makePrimary ? [clean, ...rest] : [...rest, clean]
      const normalized = normalizeImages(next)
      const idx = normalized.indexOf(clean)
      setSelectedImageIndex(idx === -1 ? 0 : idx)
      return normalized
    })
  }

  function removeListingImage(index: number) {
    setImages((prev) => {
      const normalized = normalizeImages(prev.filter((_, idx) => idx !== index))
      setSelectedImageIndex((current) => {
        if (!normalized.length) return 0
        if (current === index) return 0
        if (current > index) return Math.max(0, current - 1)
        return Math.min(current, normalized.length - 1)
      })
      return normalized
    })
  }

  function setPrimaryListingImage(index: number) {
    setImages((prev) => {
      if (index <= 0 || index >= prev.length) return prev
      const next = [...prev]
      const [selected] = next.splice(index, 1)
      next.unshift(selected)
      const normalized = normalizeImages(next)
      setSelectedImageIndex(0)
      return normalized
    })
  }

  function replaceSelectedImage(url: string) {
    const clean = (url || '').trim()
    if (!clean) return
    setImages((prev) => {
      if (!prev.length) {
        const normalized = normalizeImages([clean])
        setSelectedImageIndex(0)
        return normalized
      }
      const idx = Math.max(0, Math.min(selectedImageIndex, prev.length - 1))
      const next = [...prev]
      next[idx] = clean
      const normalized = normalizeImages(next)
      const newIndex = Math.max(0, Math.min(idx, normalized.length - 1))
      setSelectedImageIndex(newIndex)
      return normalized
    })
  }

  async function loadListingForEdit(publicId: string) {
    setLoadingListing(true)
    setError(null)
    setMessage(null)
    setFieldErrors({})
    try {
      const detail = await fetchListingDetail(publicId)
      const product = detail.product || {}
      const plain = product.description || ''
      const html = textToHtmlParagraphs(plain)
      setTitle(product.title || '')
      setSubtitle(product.subtitle || '')
      setDescriptionPlain(plain)
      setDescriptionHtml(html || '<p></p>')
      const type = product.product_type
      if (type === 'digital' || type === 'hybrid' || type === 'physical') setProductType(type)
      else setProductType('physical')
      const cond = detail.condition
      setCondition(cond === 'new' ? 'new' : 'used')
      setPrice(detail.price_pixe || '')
      setStock(detail.stock_qty != null ? String(detail.stock_qty) : '1')
      const galleryUrls = (detail.images || []).map((img: any) => img?.public_url || '').filter(Boolean)
      if (galleryUrls.length) setImagesDirect(galleryUrls)
      else if (product.thumbnail_media?.public_url) setImagesDirect([product.thumbnail_media.public_url])
      else setImagesDirect([])
      setListingActive(detail.active !== false)
      setCurrentDraftId(null)
      localStorage.removeItem(STORE_DRAFT_CURRENT_KEY)
      setTimeout(() => { richRef.current?.setHTML(html || '<p></p>') }, 0)
    } catch (err: any) {
      setError(err?.message || 'Failed to load listing for edit')
      setListingActive(true)
      setEditingListingId(null)
    } finally {
      setLoadingListing(false)
    }
  }

  async function onSave() {
    if (!authed) { nav('/login'); return }
    if (loadingListing) return
    setSaving(true)
    setError(null)
    setSellerBlockers([])
    setMessage(null)
    setFieldErrors({})
    try {
      const galleryPayload = normalizeImages(images)
      const payload = {
        title: title.trim(),
        subtitle: subtitle.trim() || undefined,
        description: (descriptionPlain || '').trim() || undefined,
        product_type: productType,
        price_pixe: parsePrice(price) ?? price,
        stock_qty: Math.max(0, parseInt(stock || '0', 10)) || 0,
        condition,
        image_url: galleryPayload[0] || undefined,
        gallery: galleryPayload,
      }
      if (editingListingId) {
        const res = await updateStoreListing(editingListingId, payload)
        const publicId = (res as any)?.listing?.public_id || editingListingId
        if (publicId) nav(`/store/i/${publicId}`)
        else setMessage('Listing updated')
      } else {
        const res = await createStoreProduct(payload)
        const publicId = (res as any)?.listing?.public_id
        if (publicId) nav(`/store/i/${publicId}`)
        else setMessage('Created (no id returned)')
      }
    } catch (e: any) {
      (() => {
        const data = (e as any)?.response?.data
        const nextFieldErrors = extractFieldErrors(data)
        if (Object.keys(nextFieldErrors).length) {
          setFieldErrors(nextFieldErrors)
        }
        let msg = (e as any)?.message || (editingListingId ? 'Update failed' : 'Create failed')
        if (data?.code === 'SELLER_NOT_ELIGIBLE' && Array.isArray(data?.blockers)) {
          setSellerBlockers(data.blockers)
          msg = 'Complete seller onboarding to publish your listing.'
        }
        if (data) {
          if (typeof (data as any).detail === 'string') {
            const detailMsg = (data as any).detail
            if (detailMsg.toLowerCase().includes('changes before it can be published')) {
              msg = 'This listing needs changes before it can be published.'
            } else {
              msg = detailMsg
            }
          }
          else if (typeof (data as any).error === 'string') msg = (data as any).error
          else if (typeof data === 'object') {
            const k = Object.keys(data as any)[0]
            const v = (data as any)[k]
            if (Array.isArray(v) && v.length) msg = String(v[0])
            else if (typeof v === 'string') msg = v
          }
        }
        if (!msg && Object.keys(nextFieldErrors).length) {
          msg = 'Please review the highlighted fields.'
        }
        setError(msg)
      })()
    } finally {
      setSaving(false)
    }
  }

  async function onToggleListingActive() {
    if (!editingListingId) return
    const next = !listingActive
    setTogglingActive(true)
    setError(null)
    setMessage(null)
    try {
      await updateStoreListing(editingListingId, { active: next })
      setListingActive(next)
      setMessage(next ? 'Listing relisted' : 'Listing removed from store')
    } catch (e: any) {
      const data = (e as any)?.response?.data
      let msg = (e as any)?.message || 'Status update failed'
      if (data) {
        if (data?.code === 'SELLER_NOT_ELIGIBLE' && Array.isArray(data?.blockers)) {
          setSellerBlockers(data.blockers)
          msg = 'Complete seller onboarding to publish your listing.'
        } else if (typeof data.detail === 'string') msg = data.detail
        else if (typeof data.error === 'string') msg = data.error
      }
      setError(msg)
    } finally {
      setTogglingActive(false)
    }
  }

  async function onSuggestThree() {
    const base = (descriptionPlain || '').trim()
    const details = [title, subtitle, condition].filter(Boolean).join(', ')
    const instr = `Write three ${tone.toLowerCase()} product descriptions for: ${details}. ` +
      `${base ? 'Rewrite based on the current description below.' : 'Create original options.'} ` +
      `Focus on condition, key features, and what’s included. 60–140 words each. Separate with a line containing three dashes (---).`
    try {
      const res = await ai.run({ prompt: instr, body: base || undefined })
      const text = (res as any)?.content || ''
      const parts = String(text).split(/\n\s*---\s*\n/).map(s => s.trim()).filter(Boolean)
      setSuggestions(parts.length ? parts.slice(0, 3) : [text])
      setAiSuggestion('')
      setError(null)
    } catch (e: any) {
      setError(e?.message || ai.error || 'AI request failed')
    }
  }

  async function onRunAI() {
    const prompt = aiPrompt.trim()
    if (!prompt) return
    try {
      const res = await ai.run({ prompt, body: descriptionPlain })
      setAiSuggestion((res as any)?.content || '')
      setSuggestions([])
      setError(null)
    } catch (e: any) {
      setError(e?.message || ai.error || 'AI request failed')
    }
  }

  function applySuggestion(s: string, mode: 'replace' | 'append') {
    if (!s) return
    if (mode === 'replace') replaceWithText(s)
    else appendText(s)
  }

  function applyDraft(draft: StoreDraftItem, id?: string) {
    setTitle(draft.title || '')
    setSubtitle(draft.subtitle || '')
    setPrice(draft.price || '')
    setStock(draft.stock || '1')
    setCondition(draft.condition || 'used')
    setProductType(draft.productType || 'physical')
    const html = draft.descriptionHtml || '<p></p>'
    setDescriptionHtml(html)
    setDescriptionPlain(draft.descriptionPlain || '')
    if (draft.images?.length) setImagesDirect(draft.images)
    else if (draft.imageUrl) setImagesDirect([draft.imageUrl])
    else setImagesDirect([])
    setImgPrompt(draft.imgPrompt || STORE_POLISH_DEFAULT)
    setCurrentDraftId(id || draft.id)
    localStorage.setItem(STORE_DRAFT_CURRENT_KEY, id || draft.id)
    setTimeout(() => richRef.current?.setHTML(html), 0)
    setMessage(`Draft loaded: ${draft.title || 'Untitled listing'}`)
  }

  function onSaveDraft() {
    const now = new Date().toISOString()
    let id = currentDraftId
    if (!id) id = `store-${Date.now()}`
    const html = richRef.current?.getHTML() || descriptionHtml || '<p></p>'
    const plain = richRef.current?.getText()?.replace(/\u00A0/g, ' ').trim() || descriptionPlain || ''
    const existing = id ? loadStoreDraft(id) : null
    const galleryPayload = images
    const item: StoreDraftItem = {
      id,
      title,
      subtitle,
      price,
      stock,
      condition,
      productType,
      descriptionHtml: html,
      descriptionPlain: plain,
      imageUrl: galleryPayload[0] || '',
      images: galleryPayload,
      imgPrompt,
      created_at: existing?.created_at || now,
      updated_at: now,
    }
    saveStoreDraftItem(item)
    const idx = loadStoreDraftsIndex()
    const found = idx.find((d) => d.id === id)
    if (found) {
      found.title = title || found.title
      found.updated_at = now
    } else {
      idx.unshift({ id, title: title || 'Untitled listing', updated_at: now })
    }
    saveStoreDraftsIndex(idx)
    setDraftsList(idx)
    setCurrentDraftId(id)
    localStorage.setItem(STORE_DRAFT_CURRENT_KEY, id)
    setMessage('Draft saved')
  }

  function onSelectDraft(id: string) {
    if (!id) return
    const draft = loadStoreDraft(id)
    if (draft) applyDraft(draft, id)
  }

  function onDeleteDraft() {
    if (!currentDraftId) return
    removeStoreDraftItem(currentDraftId)
    const updated = loadStoreDraftsIndex().filter((d) => d.id !== currentDraftId)
    saveStoreDraftsIndex(updated)
    setDraftsList(updated)
    setCurrentDraftId(null)
    localStorage.removeItem(STORE_DRAFT_CURRENT_KEY)
    setMessage('Draft deleted')
  }

  function generateMockImage() {
    const seed = encodeURIComponent((imgPrompt || title || 'product').slice(0, 60))
    const url = `https://picsum.photos/seed/${seed}/1600/1000`
    addListingImage(url, images.length === 0)
  }

  async function fetchImageAsBase64(url: string): Promise<{ data: string; mime: string }> {
    const params = new URLSearchParams({ url })
    const res = await api.get<{ data: string; mime: string }>(`/api/pixe_store/store/images/proxy/?${params.toString()}`)
    if (!res?.data?.data) throw new Error('Failed to load the selected photo')
    return { data: res.data.data, mime: res.data.mime || 'image/png' }
  }

  async function onGenerateAIImage() {
    const selectedUrl = images[selectedImageIndex]
    if (!selectedUrl) {
      setAiImageError('Upload or select a photo before using AI polish.')
      return
    }
    const base = [title, subtitle].filter(Boolean).join(' — ') || 'Product'
    const polishBrief = [
      'You must edit the provided product photo only — reuse the exact pixels of the subject.',
      'Do NOT change or replace the object, parts, spokes, tread, geometry, or camera angle. No stock replacements.',
      'Only improve lighting, remove noise/clutter, smooth blemishes, and place it on a clean studio-ready backdrop.',
      'Match the background style to the other listing photos: same color, lighting, and studio look so the set feels like one shoot.',
      'Keep the exact product shape, components, proportions, and colors. Do not redraw or replace the item. No logos, watermarks, added accessories, text, or new objects.'
    ].join(' ')
    const prompt = `${polishBrief} Product details: ${base}. Additional direction: ${imgPrompt || 'clean studio background, soft light, minimal shadows.'}`
    setAiImageError(null)
    setAiImages([])
    setAiImageLoading(true)
    try {
      const source = await fetchImageAsBase64(selectedUrl)
      const res = await generateCoverImage({
        prompt,
        size: '1024x1024',
        quality: 'standard',
        responseFormat: 'b64_json',
        sourceImage: source.data,
        sourceImageMime: source.mime,
      })
      const images = Array.isArray(res.images) ? res.images : []
      setAiImages(images)
    } catch (e: any) {
      setAiImageError(e?.message || 'AI image failed')
    } finally {
      setAiImageLoading(false)
    }
  }

  async function onUploadFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setError(null)
    setMessage('Uploading...')
    try {
      const uploaded = await uploadCoverDirect(file, {
        upload_content_type: 'listing_image',
        target_field: 'image_url',
        object_type: 'pixe_store.upload',
      })
      if (uploaded?.public_url) addListingImage(uploaded.public_url, images.length === 0)
      setMessage('Uploaded ✓')
    } catch (err: any) {
      setError(err?.message || 'Upload failed')
      setMessage(null)
    }
  }

  function onAddImageByUrl() {
    const raw = window.prompt('Paste image URL (https://...)')?.trim()
    if (!raw) return
    addListingImage(raw, images.length === 0)
  }

  function replaceWithText(text: string) {
    if (!text) return
    const html = textToHtmlParagraphs(text)
    richRef.current?.setHTML(html)
    setDescriptionHtml(html)
    setDescriptionPlain(text)
  }

  function appendText(text: string) {
    if (!text) return
    richRef.current?.appendHTML(textToHtmlParagraphs(text))
  }

  const refreshToolbar = () => { richRef.current?.refreshToolbarState() }
  const syncEditor = () => {
    const html = richRef.current?.getHTML() || '<p></p>'
    const text = richRef.current?.getText()?.replace(/\u00A0/g, ' ').trim() || ''
    setDescriptionHtml(html)
    setDescriptionPlain(text)
  }
  function toggleBold() { richRef.current?.toggleBold(); refreshToolbar() }
  function toggleItalic() { richRef.current?.toggleItalic(); refreshToolbar() }
  function toggleUnderline() { richRef.current?.toggleUnderline(); refreshToolbar() }
  function toggleStrike() { richRef.current?.toggleStrike(); refreshToolbar() }
  function toggleCode() { richRef.current?.toggleCode(); refreshToolbar() }
  function setH1() { richRef.current?.setHeading(1); refreshToolbar() }
  function setH2() { richRef.current?.setHeading(2); refreshToolbar() }
  function setH3() { richRef.current?.setHeading(3); refreshToolbar() }
  function setH4() { richRef.current?.setHeading(4); refreshToolbar() }
  function toggleBullet() { richRef.current?.toggleBulletList(); refreshToolbar() }
  function toggleOrdered() { richRef.current?.toggleOrderedList(); refreshToolbar() }
  function toggleQuote() { richRef.current?.toggleBlockquote(); refreshToolbar() }
  function toggleCodeBlock() { richRef.current?.toggleCodeBlock(); refreshToolbar() }
  function insertDivider() { richRef.current?.insertDivider(); syncEditor(); refreshToolbar() }
  function addLink() {
    const url = window.prompt('Enter URL (https://...)')?.trim()
    if (!url) return
    const hasSelection = !!richRef.current?.getSelectionText()
    if (hasSelection) { richRef.current?.setLink(url) }
    else { richRef.current?.appendHTML(`<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer nofollow">${escapeHtml(url)}</a>`) }
    syncEditor()
  }
  function removeLink() { richRef.current?.unsetLink(); syncEditor() }
  function addImage() {
    const url = window.prompt('Image URL (https://...)')?.trim()
    if (!url) return
    const alt = window.prompt('Alt text (optional)')?.trim() || ''
    richRef.current?.insertImage(url, alt)
    syncEditor()
  }
  function alignLeft() { richRef.current?.setTextAlign('left'); refreshToolbar() }
  function alignCenter() { richRef.current?.setTextAlign('center'); refreshToolbar() }
  function alignRight() { richRef.current?.setTextAlign('right'); refreshToolbar() }
  function alignJustify() { richRef.current?.setTextAlign('justify'); refreshToolbar() }
  function undo() { richRef.current?.undo(); syncEditor(); refreshToolbar() }
  function redo() { richRef.current?.redo(); syncEditor(); refreshToolbar() }
  function clearFormatting() { richRef.current?.clearFormatting(); syncEditor(); refreshToolbar() }
  function toggleHighlight() { richRef.current?.toggleHighlight(); refreshToolbar() }

  async function onSuggestTitle() {
    const base = [subtitle, descriptionPlain.slice(0, 200)].filter(Boolean).join('\n')
    const prompt = `Propose three concise product titles based on the details. Keep under 60 characters each. Output three lines only.`
    try {
      const res = await ai.run({ prompt, body: [title, base].filter(Boolean).join('\n') })
      const text = (res as any)?.content || ''
      const parts = text.split(/\r?\n+/).map(s => s.trim()).filter(Boolean).slice(0, 3)
      setTitleOptions(parts)
    } catch (e: any) {
      setError(e?.message || ai.error || 'AI request failed')
    }
  }

  async function onSuggestPrice() {
    const details = [title, subtitle, condition, productType, descriptionPlain.slice(0, 300)].filter(Boolean).join('\n')
    const prompt = `Suggest a fair price in PIXE based on the details. Reply with only the number like 49.00.`
    try {
      const res = await ai.run({ prompt, body: details })
      const text = (res as any)?.content || ''
      const m = text.match(/([0-9]+(?:\.[0-9]{1,2})?)/)
      if (m) { setPrice(m[1]); setPriceSuggest(m[1]) } else { setPriceSuggest(text.trim() || null) }
    } catch (e: any) {
      setError(e?.message || ai.error || 'AI request failed')
    }
  }

  return (
    <div className="min-h-screen">
      <NavBar showSearch={false} />
      <main className="mx-auto max-w-7xl px-4 py-6">
        {!authed && (
          <div className="mb-4 text-sm text-amber-300">Please <Link className="underline" to="/login">log in</Link> to list a product.</div>
        )}
        {blockers.length > 0 && (
          <div className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-white/85">
            <div className="font-semibold">Finish seller onboarding to publish</div>
            {publishBlocked && (
              <div className="mt-1 text-white/70">This listing will stay hidden until the steps below are complete.</div>
            )}
            <div className="mt-2 space-y-1">
              {blockers.map((b) => (
                <div key={b.code} className="text-white/75">{b.message}</div>
              ))}
            </div>
            <div className="mt-3">
              <Link to="/settings/integrations" className="inline-flex items-center rounded-lg border border-white/20 px-3 py-1.5 text-xs font-semibold text-white/90 hover:bg-white/10">
                Open seller onboarding
              </Link>
            </div>
          </div>
        )}
        {editingListingId && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/80">
            <div>
              <div className="font-semibold text-white">Editing existing listing</div>
              <div className="text-xs text-white/60">Public ID: {editingListingId}</div>
              <div className="text-xs text-white/60">
                Status:{' '}
                <span className={listingActive ? 'text-emerald-300' : 'text-amber-300'}>
                  {listingActive ? 'Listed' : 'Unlisted'}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={onToggleListingActive}
                disabled={togglingActive || loadingListing}
                className="rounded-lg border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-semibold hover:bg-white/20 disabled:opacity-50"
              >
                {togglingActive ? 'Updating...' : listingActive ? 'Delist' : 'Relist'}
              </button>
              <Link to="/store/list/new" className="text-xs font-semibold text-brand hover:underline">Start new listing</Link>
            </div>
          </div>
        )}
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-xl font-extrabold">{editingListingId ? 'Edit Product Listing' : 'Create Product Listing'}</h1>
          <div className="flex items-center gap-2">
            {loadingListing && <div className="text-xs text-white/60">Loading listing...</div>}
            {message && <div className="text-sm text-white/70">{message}</div>}
            {error && <div className="text-sm text-red-400">{error}</div>}
            <button onClick={onSave} disabled={!authed || saving || loadingListing} className="px-4 py-2 rounded-lg bg-brand text-white font-semibold hover:bg-brand-600 disabled:opacity-60">
              {saving ? 'Saving...' : saveLabel}
            </button>
          </div>
        </div>
        <DraftControls
          onSaveDraft={onSaveDraft}
          currentDraftId={currentDraftId}
          draftsList={draftsList}
          onSelectDraft={onSelectDraft}
          onDeleteDraft={onDeleteDraft}
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Editor */}
          <section className="lg:col-span-7 space-y-4">
            <div className="rounded-xl border border-white/10 p-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="block">
                    <div className="text-sm text-white/70 mb-1">Title</div>
                    <div className="flex items-center gap-2">
                      <input
                        value={title}
                        onChange={(e)=>setTitle(e.target.value)}
                        className={`w-full rounded-lg bg-white/5 border px-3 py-2 outline-none focus:ring-2 ${
                          fieldErrors.title ? 'border-rose-400/60 focus:ring-rose-500/40' : 'border-white/10 focus:ring-brand'
                        }`}
                      />
                      <button type="button" onClick={onSuggestTitle} className="px-2.5 py-1.5 text-xs rounded-md border border-white/10 bg-white/5 hover:bg-white/10">AI</button>
                    </div>
                    {titleOptions.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {titleOptions.map((t,i)=> (
                          <button key={i} onClick={()=>{ setTitle(t); setTitleOptions([]) }} className="px-2 py-1 text-xs rounded-full border border-white/10 hover:bg-white/10">{t}</button>
                        ))}
                      </div>
                    )}
                    {fieldErrors.title && <div className="mt-1 text-xs text-rose-300">{fieldErrors.title}</div>}
                  </label>
                  <label className="block">
                    <div className="text-sm text-white/70 mb-1">Subtitle (optional)</div>
                    <input
                      value={subtitle}
                      onChange={(e)=>setSubtitle(e.target.value)}
                      className={`w-full rounded-lg bg-white/5 border px-3 py-2 outline-none focus:ring-2 ${
                        fieldErrors.subtitle ? 'border-rose-400/60 focus:ring-rose-500/40' : 'border-white/10 focus:ring-brand'
                      }`}
                    />
                    {fieldErrors.subtitle && <div className="mt-1 text-xs text-rose-300">{fieldErrors.subtitle}</div>}
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <label className="block">
                    <div className="text-sm text-white/70 mb-1">Price (PIXE)</div>
                    <div className="flex items-center gap-2">
                      <input
                        value={price}
                        onChange={(e)=>setPrice(e.target.value)}
                        placeholder="49.00"
                        className={`w-full rounded-lg bg-white/5 border px-3 py-2 outline-none focus:ring-2 ${
                          fieldErrors.price_pixe ? 'border-rose-400/60 focus:ring-rose-500/40' : 'border-white/10 focus:ring-brand'
                        }`}
                      />
                      <button type="button" onClick={onSuggestPrice} className="px-2.5 py-1.5 text-xs rounded-md border border-white/10 bg-white/5 hover:bg-white/10">AI</button>
                    </div>
                    {priceSuggest && <div className="text-xs text-white/70 mt-1">Suggested: {fmtPixe(parsePrice(priceSuggest)) || `${priceSuggest} PIXE`}</div>}
                    {fieldErrors.price_pixe && <div className="mt-1 text-xs text-rose-300">{fieldErrors.price_pixe}</div>}
                  </label>
                  <label className="block">
                    <div className="text-sm text-white/70 mb-1">Stock</div>
                    <input
                      value={stock}
                      onChange={(e)=>setStock(e.target.value)}
                      placeholder="1"
                      className={`w-full rounded-lg bg-white/5 border px-3 py-2 outline-none focus:ring-2 ${
                        fieldErrors.stock_qty ? 'border-rose-400/60 focus:ring-rose-500/40' : 'border-white/10 focus:ring-brand'
                      }`}
                    />
                    {fieldErrors.stock_qty && <div className="mt-1 text-xs text-rose-300">{fieldErrors.stock_qty}</div>}
                  </label>
                  <label className="block">
                    <div className="text-sm text-white/70 mb-1">Condition</div>
                    <div className="flex items-center gap-2">
                      {(['used','new'] as const).map((c) => (
                        <button key={c} onClick={()=>setCondition(c)} className={`px-2.5 py-1 text-xs rounded-full border ${condition===c ? 'bg-blue-500 border-transparent' : 'bg-white/5 border-white/10'}`}>{c.toUpperCase()}</button>
                      ))}
                    </div>
                    {fieldErrors.condition && <div className="mt-1 text-xs text-rose-300">{fieldErrors.condition}</div>}
                  </label>
                  <label className="block">
                    <div className="text-sm text-white/70 mb-1">Type</div>
                    <div className="flex items-center gap-2">
                      {(['physical','digital','hybrid'] as const).map((t) => (
                        <button key={t} onClick={()=>setProductType(t)} className={`px-2.5 py-1 text-xs rounded-full border ${productType===t ? 'bg-blue-500 border-transparent' : 'bg-white/5 border-white/10'}`}>{t}</button>
                      ))}
                    </div>
                    {fieldErrors.product_type && <div className="mt-1 text-xs text-rose-300">{fieldErrors.product_type}</div>}
                  </label>
                </div>

                <div>
                  <div className="font-semibold mb-2">Description</div>
                <div className="relative text-sm px-2 pt-2 pb-1 rounded-xl bg-neutral-900/60 border border-white/10 shadow-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-1 py-1">
                      <button title="Bold" aria-label="Bold" onClick={toggleBold} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.bold ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiBold /></button>
                      <button title="Italic" aria-label="Italic" onClick={toggleItalic} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.italic ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiItalic /></button>
                      <button title="Strikethrough" aria-label="Strikethrough" onClick={toggleStrike} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.strike ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><RiStrikethrough2 /></button>
                      <button title="Inline code" aria-label="Inline code" onClick={toggleCode} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.code ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiCode /></button>
                      <button title="Underline" aria-label="Underline" onClick={toggleUnderline} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.underline ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiUnderline /></button>
                    </div>
                    <div className="w-px h-6 bg-white/10" />
                    <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-1 py-1">
                      <button title="Heading 1" aria-label="Heading 1" onClick={setH1} className={`inline-flex items-center gap-1 px-2.5 h-8 rounded-md border ${tb.h1 ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiType /><span className="text-[11px]">H1</span></button>
                      <button title="Heading 2" aria-label="Heading 2" onClick={setH2} className={`inline-flex items-center gap-1 px-2.5 h-8 rounded-md border ${tb.h2 ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiType /><span className="text-[11px]">H2</span></button>
                      <button title="Heading 3" aria-label="Heading 3" onClick={setH3} className={`inline-flex items-center gap-1 px-2.5 h-8 rounded-md border ${tb.h3 ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiType /><span className="text-[11px]">H3</span></button>
                      <button title="Heading 4" aria-label="Heading 4" onClick={setH4} className={`inline-flex items-center gap-1 px-2.5 h-8 rounded-md border ${tb.h4 ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiType /><span className="text-[11px]">H4</span></button>
                    </div>
                  </div>

                  <div className="mt-1 flex flex-nowrap items-center gap-1.5">
                    <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-1 py-1">
                      <button title="Bullet list" aria-label="Bullet list" onClick={toggleBullet} className={`inline-flex items-center justify-center h-8 rounded-md px-2.5 border ${tb.bullet ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiList /></button>
                      <button title="Numbered list" aria-label="Numbered list" onClick={toggleOrdered} className={`inline-flex items-center justify-center h-8 rounded-md px-2.5 border ${tb.ordered ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><RiListOrdered /></button>
                      <button title="Quote" aria-label="Quote" onClick={toggleQuote} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.quote ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><RiDoubleQuotesL /></button>
                      <button title="Highlight" aria-label="Highlight" onClick={toggleHighlight} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.highlight ? 'bg-amber-500/30 border-amber-400/40 text-amber-100' : 'bg-amber-500/20 hover:bg-amber-500/30 border-amber-500/40 text-amber-300'}`}><RiMarkPenLine /></button>
                      <button title="Code block" aria-label="Code block" onClick={toggleCodeBlock} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.codeBlock ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><BiCodeBlock /></button>
                      <button title="Divider" aria-label="Divider" onClick={insertDivider} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90"><FiMinus /></button>
                    </div>
                    <div className="w-px h-6 bg-white/10" />
                    <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-1 py-1">
                      <button title="Align left" aria-label="Align left" onClick={alignLeft} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.align==='left' ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiAlignLeft /></button>
                      <button title="Align center" aria-label="Align center" onClick={alignCenter} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.align==='center' ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiAlignCenter /></button>
                      <button title="Align right" aria-label="Align right" onClick={alignRight} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.align==='right' ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiAlignRight /></button>
                      <button title="Justify" aria-label="Justify" onClick={alignJustify} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.align==='justify' ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiAlignJustify /></button>
                    </div>
                    <div className="w-px h-6 bg-white/10" />
                    <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-1 py-1">
                      <button title="Link" aria-label="Link" onClick={addLink} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90"><FiLink /></button>
                      <button title="Unlink" aria-label="Unlink" onClick={removeLink} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90"><FiX /></button>
                      <button title="Image" aria-label="Image" onClick={addImage} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90"><FiImage /></button>
                    </div>
                    <div className="ml-auto flex items-center gap-1.5 shrink-0 bg-white/5 rounded-lg px-1 py-1">
                      <button title="Undo" aria-label="Undo" onClick={undo} disabled={!tb.canUndo} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90 disabled:opacity-50"><FiRotateCcw /></button>
                      <button title="Redo" aria-label="Redo" onClick={redo} disabled={!tb.canRedo} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90 disabled:opacity-50"><FiRotateCw /></button>
                      <button title="Clear formatting" aria-label="Clear formatting" onClick={clearFormatting} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90"><MdFormatClear /></button>
                    </div>
                  </div>
                </div>
                <RichEditor
                  ref={richRef}
                  initialHTML={descriptionHtml}
                  onChange={(html, text)=>{ setDescriptionHtml(html); setDescriptionPlain(text) }}
                  onToolbarState={setTb}
                />
                {fieldErrors.description && <div className="mt-1 text-xs text-rose-300">{fieldErrors.description}</div>}
                {/* AI helpers */}
                <div className="mt-2 flex flex-wrap gap-2">
                  {(['Professional','Friendly','Enthusiastic','Minimal'] as const).map(t => (
                    <button key={t} onClick={()=>setTone(t)} className={`px-2.5 py-1 text-xs rounded-full border ${tone===t ? 'bg-blue-500 border-transparent' : 'bg-white/5 border-white/10'}`}>{t}</button>
                  ))}
                  <button onClick={onSuggestThree} disabled={ai.loading} className="px-2.5 py-1 text-xs rounded-full border border-white/10 bg-white/5 hover:bg-white/10 disabled:opacity-60">Suggest 3</button>
                </div>

                <div className="mt-3 w-full rounded-xl border border-white/10 bg-white/5 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold">AI edit</div>
                    {ai.loading && <div className="text-xs text-white/60">Working...</div>}
                  </div>
                  <p className="text-xs text-white/60">Describe how you want the description tweaked.</p>
                  <div className="flex items-center gap-2 rounded-lg bg-black/30 px-3 py-2 border border-white/10">
                    <input
                      value={aiPrompt}
                      onChange={(e)=>setAiPrompt(e.target.value)}
                      placeholder="E.g. tighten the intro and make it more upbeat..."
                      className="flex-1 bg-transparent text-sm text-white outline-none"
                    />
                    <button
                      onClick={onRunAI}
                      disabled={ai.loading || !aiPrompt.trim()}
                      className="inline-flex items-center rounded-md bg-brand px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
                    >
                      {ai.loading ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                </div>

                {aiSuggestion && (
                  <div className="mt-3 w-full rounded-lg border border-white/10 bg-white/5 p-3">
                    <div className="text-xs text-white/70 mb-1">AI suggestion</div>
                    <div className="text-sm whitespace-pre-wrap">{aiSuggestion}</div>
                    <div className="mt-2 flex items-center gap-2">
                      <button onClick={()=>applySuggestion(aiSuggestion,'replace')} className="px-2 py-1 text-xs rounded-md bg-blue-500 text-white">Replace</button>
                      <button onClick={()=>applySuggestion(aiSuggestion,'append')} className="px-2 py-1 text-xs rounded-md border border-white/10 bg-white/5">Append</button>
                    </div>
                  </div>
                )}

                {suggestions.length>0 && (
                  <div className="mt-3 space-y-3">
                    {suggestions.map((s,i)=>(
                      <div key={i} className="rounded-lg border border-white/10 bg-white/5">
                        <div className="px-3 py-2 border-b border-white/10 text-xs text-white/70">Option {i+1}</div>
                        <div className="p-3 text-sm whitespace-pre-wrap">{s}</div>
                        <div className="px-3 py-2 border-t border-white/10 flex items-center gap-2">
                          <button onClick={()=>applySuggestion(s,'replace')} className="px-2 py-1 text-xs rounded-md bg-blue-500 text-white">Use</button>
                          <button onClick={()=>applySuggestion(s,'append')} className="px-2 py-1 text-xs rounded-md border border-white/10 bg-white/5">Append</button>
                        </div>
                      </div>
                    ))}
                    <div className="flex justify-end">
                      <button onClick={onSuggestThree} disabled={ai.loading} className="px-3 py-1.5 text-xs rounded-full border border-white/10 bg-white/5 hover:bg-white/10 disabled:opacity-60">Re‑roll</button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-white/10 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="font-semibold">Product photos</div>
                {images.length > 0 && (
                  <div className="text-xs text-white/60">
                    Selected: Photo {selectedImageIndex + 1} / {images.length}
                  </div>
                )}
              </div>
              {imageError && <div className="text-xs text-rose-300">{imageError}</div>}
              <p className="text-xs text-white/60">
                Click any photo below to preview/polish it. The selected photo is shown here and used for AI polish.
              </p>
              <div className="rounded-xl border border-white/10 overflow-hidden">
                <div className="relative aspect-[4/3] bg-white/5 flex items-center justify-center">
                  {heroImage ? (
                    <img src={heroImage} alt={title || 'Product'} className="absolute inset-0 h-full w-full object-cover" />
                  ) : (
                    <div className="text-sm text-white/50 text-center px-6">Upload or paste a photo—this will be the main image buyers see.</div>
                  )}
                  {heroImage && (
                    <span className="absolute left-3 top-3 rounded-full bg-black/60 px-3 py-1 text-xs font-semibold text-white/80">
                      Previewing photo {selectedImageIndex + 1}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 cursor-pointer">
                  <FiImage />
                  <span className="text-sm">Upload photo</span>
                  <input type="file" accept="image/*" className="hidden" onChange={onUploadFile} />
                </label>
                <button type="button" onClick={onAddImageByUrl} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-sm">
                  Paste image URL
                </button>
                <button type="button" onClick={generateMockImage} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-sm">
                  Use placeholder
                </button>
              </div>
              {images.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs text-white/70">Gallery</div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {images.map((url, idx) => (
                      <div
                        key={`${url}-${idx}`}
                        onClick={() => setSelectedImageIndex(idx)}
                        className={`rounded-lg border bg-white/5 overflow-hidden cursor-pointer transition ${
                          selectedImageIndex === idx ? 'border-brand shadow-[0_0_0_2px_rgba(99,102,241,0.45)]' : 'border-white/10'
                        }`}
                      >
                        <div className="relative aspect-square">
                          <img src={url} alt={`Photo ${idx + 1}`} className="absolute inset-0 h-full w-full object-cover" />
                          {idx === 0 && (
                            <span className="absolute left-2 top-2 rounded-full bg-brand px-2 py-0.5 text-[10px] font-semibold text-white">
                              Main
                            </span>
                          )}
                          {selectedImageIndex === idx && (
                            <span className="absolute right-2 top-2 rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-semibold text-white">
                              Selected
                            </span>
                          )}
                        </div>
                        <div className="flex items-center justify-between px-2 py-1 text-xs">
                          <div className="text-white/70 truncate">{idx === 0 ? 'Primary' : `Photo ${idx + 1}`}</div>
                          <div className="flex items-center gap-1">
                            {idx > 0 && (
                              <button
                                type="button"
                                onClick={(e)=>{e.stopPropagation(); setPrimaryListingImage(idx)}}
                                className="rounded border border-white/20 px-2 py-0.5 hover:bg-white/10"
                              >
                                Set main
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={(e)=>{e.stopPropagation(); removeListingImage(idx)}}
                              className="rounded border border-white/20 px-2 py-0.5 hover:bg-white/10"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-xs text-white/70">Upload a photo you shot—then let AI polish it into a studio-ready hero.</p>
              <div className="rounded-xl border border-white/10 bg-white/5 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold">AI polish</div>
                  {aiImageLoading && <div className="text-xs text-white/60">Working...</div>}
                </div>
                <div className="text-xs text-white/60">
                  {images.length
                    ? `Using photo ${selectedImageIndex + 1}. Update the prompt and hit Polish to generate cleaner looks.`
                    : 'Add a photo above to polish it with AI.'}
                </div>
                <div className="flex items-center gap-2 rounded-lg bg-black/30 px-3 py-2 border border-white/10">
                  <input
                    value={imgPrompt}
                    onChange={(e)=>setImgPrompt(e.target.value)}
                    placeholder="Studio background, soft light..."
                    className="flex-1 bg-transparent text-sm text-white outline-none"
                  />
                  <button
                    onClick={onGenerateAIImage}
                    disabled={aiImageLoading}
                    className="inline-flex items-center rounded-md bg-brand px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
                  >
                    {aiImageLoading ? 'Polishing...' : 'Polish'}
                  </button>
                </div>
                {aiImageError && <div className="text-xs text-red-400">{aiImageError}</div>}
                {aiImages.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 pt-2">
                    {aiImages.map((img, idx) => {
                      const src = (img as any).public_url || (img as any).url
                      const b64 = (img as any).b64_json ? `data:image/png;base64,${(img as any).b64_json}` : null
                      const final = src || b64 || ''
                      if (!final) return null
                      return (
                        <div key={idx} className="group relative aspect-square rounded-lg overflow-hidden border border-white/10">
                          <img src={final} className="absolute inset-0 h-full w-full object-cover" />
                          <div className="absolute inset-0 flex flex-col justify-end gap-1 bg-gradient-to-t from-black/80 via-transparent to-transparent p-2 opacity-0 group-hover:opacity-100">
                            <button type="button" onClick={()=>replaceSelectedImage(final)} className="text-xs rounded bg-white/95 text-black px-2 py-1 font-semibold">
                              Replace selected
                            </button>
                            <button type="button" onClick={()=>addListingImage(final, true)} className="text-xs rounded border border-white/70 bg-black/40 text-white px-2 py-1">
                              Set main
                            </button>
                            <button type="button" onClick={()=>addListingImage(final, false)} className="text-xs rounded border border-white/50 text-white px-2 py-1">
                              Add to gallery
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Preview */}
          <aside className="lg:col-span-5">
            <div className="rounded-xl border border-white/10 overflow-hidden mb-4">
              <div className="relative aspect-[16/12] bg-white/5">
                {heroImage && <img src={heroImage} alt={title || 'Product'} className="absolute inset-0 h-full w-full object-cover" />}
              </div>
              <div className="p-3">
                <div className="text-lg font-extrabold">{title || 'Your product title'}</div>
                {subtitle && <div className="text-sm text-white/80">{subtitle}</div>}
                <div className="mt-1 text-brand font-semibold">{fmtPixe(parsePrice(price)) || (price ? `${price} PIXE` : '')}</div>
                <div className="mt-3 text-sm prose prose-invert max-w-none">
                  {descriptionHtml && descriptionHtml.trim() !== '<p></p>' ? (
                    <div dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
                  ) : (
                    <div className="text-white/70">Your description preview will appear here.</div>
                  )}
                </div>
                <div className="mt-3 text-xs text-white/70">Condition: {condition.toUpperCase()} • Stock: {stock || 0}</div>
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  )
}

function fmtPixe(p?: number | null): string | null {
  if (p === null || p === undefined) return null
  const n = typeof p === 'number' ? p : parseFloat(String(p))
  if (!isFinite(n)) return null
  return `${n.toFixed(2)} PIXE`
}

function textToHtmlParagraphs(text: string): string {
  const parts = (text || '').split(/\r?\n+/).filter(Boolean)
  if (parts.length === 0) return '<p></p>'
  return parts.map((p) => `<p>${escapeHtml(p)}</p>`).join('')
}

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

type StoreDraftItem = {
  id: string
  title: string
  subtitle: string
  price: string
  stock: string
  condition: 'new' | 'used'
  productType: 'physical' | 'digital' | 'hybrid'
  descriptionHtml: string
  descriptionPlain: string
  imageUrl?: string
  images?: string[]
  imgPrompt?: string
  created_at: string
  updated_at: string
}

function loadStoreDraftsIndex(): Array<{ id: string; title: string; updated_at: string }> {
  try {
    return JSON.parse(localStorage.getItem(STORE_DRAFT_INDEX_KEY) || '[]')
  } catch {
    return []
  }
}

function saveStoreDraftsIndex(list: Array<{ id: string; title: string; updated_at: string }>) {
  localStorage.setItem(STORE_DRAFT_INDEX_KEY, JSON.stringify(list))
}

function loadStoreDraft(id: string): StoreDraftItem | null {
  try {
    const raw = localStorage.getItem(`${STORE_DRAFT_ITEM_PREFIX}${id}`)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function saveStoreDraftItem(item: StoreDraftItem) {
  localStorage.setItem(`${STORE_DRAFT_ITEM_PREFIX}${item.id}`, JSON.stringify(item))
}

function removeStoreDraftItem(id: string) {
  localStorage.removeItem(`${STORE_DRAFT_ITEM_PREFIX}${id}`)
}

